import { useMutation } from "@apollo/client/react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import {
  GetUsersDocument,
  UpdateUserOrganizationDocument,
  type UpdateUserOrganizationMutation,
  type UpdateUserOrganizationMutationVariables,
} from "~/features/admin/users/gqls";
import { translateError } from "~/lib/errors";

interface UserOrganizationUpdateInput {
  userId: string;
  organizationShortCode: string;
}

type UserOrganizationUpdateOutcome =
  | { status: "idle" }
  | { status: "success" }
  | { status: "requires-confirmation"; message: string }
  | { status: "error" };

export function useUserOrganizationUpdate() {
  const { t } = useTranslation();
  const [outcome, setOutcome] = useState<UserOrganizationUpdateOutcome>({ status: "idle" });
  const [updateOrganization, { loading: processing }] = useMutation<
    UpdateUserOrganizationMutation,
    UpdateUserOrganizationMutationVariables
  >(UpdateUserOrganizationDocument, {
    refetchQueries: [GetUsersDocument],
    awaitRefetchQueries: true,
    update: (cache, { data }) => {
      if (data?.cnutUpdateUserOrganization.__typename !== "UserUpdateOrganizationOutput") return;

      // Moving an owner also moves their tenants. Evict inactive cache-first
      // tenant queries so the next visit does not render the previous organization.
      cache.evict({ id: "ROOT_QUERY", fieldName: "tenants" });
      cache.evict({ id: "ROOT_QUERY", fieldName: "tenantsByPk" });
      cache.gc();
    },
  });

  const submit = useCallback(
    async (input: UserOrganizationUpdateInput, removeMembershipsOutsideDestination: boolean) => {
      setOutcome({ status: "idle" });
      try {
        const response = await updateOrganization({
          variables: {
            ...input,
            removeMembershipsOutsideDestination,
          },
        });
        const result = response.data?.cnutUpdateUserOrganization;

        if (result?.__typename === "UserUpdateOrganizationOutput") {
          setOutcome({ status: "success" });
          return;
        }

        if (result?.__typename === "ValidationError") {
          const requiresConfirmation = result.fields.some(
            (field) => field.path === "removeMembershipsOutsideDestination",
          );
          if (!removeMembershipsOutsideDestination && requiresConfirmation) {
            setOutcome({ status: "requires-confirmation", message: result.message });
            return;
          }

          toast.error(result.message);
          setOutcome({ status: "error" });
          return;
        }

        throw new Error("Invalid response type");
      } catch (error) {
        toast.error(translateError(t, error));
        setOutcome({ status: "error" });
      }
    },
    [t, updateOrganization],
  );

  const reset = useCallback(() => setOutcome({ status: "idle" }), []);

  return { submit, reset, processing, outcome };
}
